import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import Icon from '../components/Icon';
import { fetchComplianceReports } from '../services/api';
import { timeAgo } from '../utils/format';

const TYPE_BADGE = {
  hourly: { class: 'badge-info',    label: 'Hourly' },
  eod:    { class: 'badge-warning', label: 'EOD'    },
};

const AgentReports = () => {
  const { environment } = useOutletContext();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchComplianceReports(undefined, environment)
      .then((data) => {
        if (cancelled) return;
        setReports(data || []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load reports');
        setReports([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [environment]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return reports.filter((r) => {
      if (typeFilter !== 'all' && r.report_type !== typeFilter) return false;
      if (!q) return true;
      return (r.summary_text || '').toLowerCase().includes(q);
    });
  }, [reports, typeFilter, query]);

  const counts = useMemo(() => ({
    hourly: reports.filter((r) => r.report_type === 'hourly').length,
    eod:    reports.filter((r) => r.report_type === 'eod').length,
    flags:  reports.reduce((s, r) => s + (parseInt(r.flags_referenced, 10) || 0), 0),
    txns:   reports.reduce((s, r) => s + (parseInt(r.transactions_reviewed, 10) || 0), 0),
  }), [reports]);

  const trigger = (kind) => {
    setBusy(true);
    setTimeout(() => {
      setBusy(false);
      // eslint-disable-next-line no-alert
      alert(`${kind} scan triggered. In production, the scheduled Vercel Cron (or this manual trigger) calls the Claude agent and writes a report to compliance_reports.`);
    }, 600);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>
            <span className="live-dot" style={{ marginRight: 12, verticalAlign: 'middle' }} />
            Agent Reports
          </h1>
          <p>
            Hourly and EOD reports from the Claude compliance agent
            <span className="text-tx3"> · {environment}</span>
          </p>
        </div>
        <div className="row row-gap-2">
          <button type="button" className="btn btn-outline" disabled={busy} onClick={() => trigger('Hourly')}>
            {busy ? 'Running…' : 'Run Hourly Scan'}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => trigger('EOD')}>
            {busy ? 'Running…' : 'Generate EOD'}
          </button>
        </div>
      </div>

      <div className="dashboard-grid stagger mb-4">
        <div className="card">
          <div className="card-title">Hourly Reports</div>
          <div className="stat-value" style={{ color: 'var(--info)' }}>{counts.hourly}</div>
          <div className="stat-label">In current view</div>
        </div>
        <div className="card">
          <div className="card-title">EOD Reports</div>
          <div className="stat-value" style={{ color: 'var(--warn)' }}>{counts.eod}</div>
          <div className="stat-label">Daily summaries</div>
        </div>
        <div className="card">
          <div className="card-title">Flags Surfaced</div>
          <div className="stat-value" style={{ color: 'var(--err)' }}>{counts.flags}</div>
          <div className="stat-label">Across all reports</div>
        </div>
        <div className="card">
          <div className="card-title">Transactions Reviewed</div>
          <div className="stat-value">{counts.txns.toLocaleString()}</div>
          <div className="stat-label">By the agent</div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-title">Filter</div>
        <div className="row row-gap-3" style={{ flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx3)' }}>
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search reports..."
              aria-label="Search agent reports"
              style={{
                width: '100%',
                padding: '10px 14px 10px 36px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--bd)',
                borderRadius: 'var(--r-md)',
                color: 'var(--tx1)',
                fontSize: 13,
                outline: 'none',
              }}
            />
          </div>
          <div className="row row-gap-2">
            {[
              { id: 'all',    label: 'All'    },
              { id: 'hourly', label: 'Hourly' },
              { id: 'eod',    label: 'EOD'    },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTypeFilter(opt.id)}
                className={clsx('btn', typeFilter === opt.id ? 'btn-primary' : 'btn-ghost')}
                style={{ padding: '8px 14px', fontSize: 12 }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
          {error}
        </div>
      )}

      {loading && reports.length === 0 ? (
        <div className="col col-gap-2">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 84 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon"><Icon name="bell" size={26} /></div>
            <h3>No reports yet</h3>
            <p>{reports.length === 0
              ? `No agent reports on ${environment}. The hourly/EOD cron will populate this once configured.`
              : 'Try clearing filters or switching report type.'}</p>
          </div>
        </div>
      ) : (
        <div className="stagger" style={{ display: 'grid', gap: 8 }}>
          {filtered.map((report) => {
            const isEOD = report.report_type === 'eod';
            return (
              <div key={report.id} className={clsx('agent-card', isEOD && 'warning')}>
                <div className="agent-time row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="row row-gap-2" style={{ alignItems: 'center' }}>
                    <span className={clsx('badge', TYPE_BADGE[report.report_type]?.class || 'badge-muted')}>
                      {TYPE_BADGE[report.report_type]?.label || report.report_type}
                    </span>
                    <span>{report.created_at ? new Date(report.created_at).toLocaleString() : '—'}</span>
                    <span style={{ color: 'var(--tx3)' }}>· {timeAgo(report.created_at)}</span>
                  </div>
                  <div className="row row-gap-3 text-tx3 text-xs">
                    {report.transactions_reviewed != null && <span>📊 {report.transactions_reviewed} txns</span>}
                    {report.flags_referenced > 0 && (
                      <span style={{ color: 'var(--warn)' }}>⚑ {report.flags_referenced} flags</span>
                    )}
                  </div>
                </div>
                <div className="agent-text">{report.summary_text}</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default AgentReports;
